// src/pages/SearchPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ARTICLES } from '../content/articles.mjs';
import SearchBar from '../components/SearchBar';
import SearchResults from '../components/SearchResults';
import SourcesTab from '../components/SourcesTab';
import HistoryTab from '../components/HistoryTab';
import ApiStatusTab from '../components/ApiStatusTab';
import { usePageMeta } from '../lib/seo';

function SearchPage() {
  const [activeTab, setActiveTab] = useState('results');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [apiStatus, setApiStatus] = useState({
    mpr: 'unknown',
    sahpra: 'unknown',
    database: 'unknown'
  });
  const [metadata, setMetadata] = useState(null);

  usePageMeta({
    title: 'Search South African Medicine Prices & Registration',
    description:
      'Free live search of South African medicine data: Single Exit Prices from the Medicine Price Registry and registration status from SAHPRA. Search by name, active ingredient, NAPPI code or symptom.',
    path: '/',
  });

  const [filters, setFilters] = useState({
    controlledOnly: false,
    approvedOnly: true,
    availableOnly: false,
    liveData: true,
    symptoms: [],
    useMpr: true,
    useSahpra: true,
    useDatabase: true,
    forceFresh: false,
    cacheExpiry: 24
  });

  // Determine if we're running on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBaseUrl = isLocalhost ? 'http://localhost:5000' : '';

  // Check API health on startup
  useEffect(() => {
    const checkApiHealth = async () => {
      try {
        // Check API server health
        const response = await fetch(`${apiBaseUrl}/api/health`);
        if (response.ok) {
          setApiStatus(prev => ({ ...prev, database: 'up' }));
        } else {
          setApiStatus(prev => ({ ...prev, database: 'down' }));
        }
      } catch (err) {
        console.error('API health check failed:', err);
        setApiStatus(prev => ({ ...prev, database: 'down' }));
      }
    };

    if (isLocalhost) {
      console.log('Running directly on localhost without proxy');
    }

    checkApiHealth();
  }, [apiBaseUrl, isLocalhost]);

  // Load search history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('searchHistory');
    if (savedHistory) {
      try {
        setSearchHistory(JSON.parse(savedHistory));
      } catch (err) {
        console.error('Failed to parse search history:', err);
      }
    }
  }, []);

  // Save search history to localStorage
  const saveToHistory = (searchTerm, searchResults) => {
    const newHistory = [
      {
        query: searchTerm,
        timestamp: new Date().toISOString(),
        resultCount: searchResults.length,
        filters: { ...filters }
      },
      ...searchHistory.filter(h => h.query !== searchTerm).slice(0, 19) // Keep only 20 most recent
    ];

    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  };

  // Perform search with real API (medicine name, selected symptoms, or both)
  const handleSearch = async () => {
    const hasSymptoms = (filters.symptoms || []).length > 0;
    if (!query.trim() && !hasSymptoms) return;

    setLoading(true);
    setError(null);
    setActiveTab('results');

    try {
      // Determine endpoint based on filters
      const endpoint = `${apiBaseUrl}/api/search`;

      console.log('Sending search request to:', endpoint);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          searchType: filters.liveData ? 'regular' : 'fulltext',
          filters: {
            ...filters,
            cacheExpiry: parseInt(filters.cacheExpiry) || 24
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`Search failed with status: ${response.status}`);
      }

      const data = await response.json();

      // Handle the response format (results array or object with results property)
      const resultsArray = data.results || data;
      const responseMetadata = data.metadata || {};

      setResults(resultsArray);
      setMetadata(responseMetadata);

      // Save to search history (label symptom-only searches by their symptoms)
      saveToHistory(query.trim() || `Symptoms: ${(filters.symptoms || []).join(', ')}`, resultsArray);

      // Update API status based on response
      if (responseMetadata.sources) {
        setApiStatus({
          mpr: responseMetadata.sources.mpr ? 'up' : 'unknown',
          sahpra: responseMetadata.sources.sahpra ? 'up' : 'unknown',
          database: 'up'
        });
      }

    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to perform search');
      setResults([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };

  // Handle history search selection
  const handleHistorySearch = (historyItem) => {
    setQuery(historyItem.query);
    if (historyItem.filters) {
      setFilters({
        ...filters,
        ...historyItem.filters
      });
    }
    setActiveTab('results');
    handleSearch();
  };

  return (
    <div>
      {/* Header */}
      <header className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-blue-800">SA Pharma Regulatory Search</h1>
            <p className="text-gray-600">Live search for medicines and controlled substances in South Africa</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              apiStatus.database === 'up' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              <i className={`fas fa-${apiStatus.database === 'up' ? 'wifi' : 'exclamation-triangle'} mr-2`}></i>
              {apiStatus.database === 'up' ? 'Online' : 'Offline'}
            </div>
          </div>
        </div>
      </header>

      {/* Search Section */}
      <SearchBar
        query={query}
        setQuery={setQuery}
        filters={filters}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
      />

      {/* Results Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('results')}
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'results'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-table mr-2"></i>Search Results
              {results.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {results.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('sources')}
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'sources'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-database mr-2"></i>Data Sources
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-history mr-2"></i>Search History
              {searchHistory.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-800 rounded-full">
                  {searchHistory.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('api')}
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'api'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-code mr-2"></i>API Status
              {apiStatus.database === 'down' && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                  <i className="fas fa-exclamation-triangle"></i>
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'results' && (
            <SearchResults
              results={results}
              loading={loading}
              error={error}
              metadata={metadata}
            />
          )}
          {activeTab === 'sources' && (
            <SourcesTab />
          )}
          {activeTab === 'history' && (
            <HistoryTab
              history={searchHistory}
              onSearchSelect={handleHistorySearch}
              onClearHistory={() => {
                setSearchHistory([]);
                localStorage.removeItem('searchHistory');
              }}
            />
          )}
          {activeTab === 'api' && (
            <ApiStatusTab status={apiStatus} />
          )}
        </div>
      </div>

      {/* Reference articles — internal links to the article hub for discovery/SEO */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold text-blue-800 mb-3">Reference articles</h2>
        <p className="text-gray-600 mb-4">Plain-language guides to medicines, regulation and pricing.</p>
        <ul className="space-y-2 mb-4">
          {ARTICLES.slice(0, 6).map((a) => (
            <li key={a.slug}>
              <Link to={`/articles/${a.slug}/`} className="text-blue-600 hover:underline">
                {a.title}
              </Link>
            </li>
          ))}
        </ul>
        <Link to="/articles/" className="inline-block text-sm font-medium text-blue-600 hover:underline">
          Browse all articles →
        </Link>
      </section>
    </div>
  );
}

export default SearchPage;
